import React, { memo } from "react";
import { Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as DocumentIcon } from "@assets/svg/wire-processing-document.svg";
import { bankExtractionCopy } from "@investorRequest/constants/bankExtractionCopy";

interface Props {
  activeStep: number;
  reducedMotion?: boolean;
}

const CheckGlyph = () => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 18,
      height: 18,
      borderRadius: "50%",
      border: "1px solid",
      borderColor: "warning.main",
      color: "warning.main",
      fontSize: 13,
      fontWeight: 500,
    }}
  >
    ✓
  </Box>
);

const PendingGlyph = ({ active, reducedMotion }: { active?: boolean; reducedMotion?: boolean }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: "inline-block",
      width: 18,
      height: 18,
      borderRadius: "50%",
      border: "2px solid",
      borderColor: "accent.main",
      borderTopColor: active ? "transparent" : "accent.main",
      ...(active && !reducedMotion
        ? {
            animation: "wire-step-spin 1s linear infinite",
            "@keyframes wire-step-spin": {
              to: {
                transform: "rotate(360deg)",
              },
            },
          }
        : {}),
    }}
  />
);

const ActiveGlyph = ({ reducedMotion }: { reducedMotion?: boolean }) => (
  <PendingGlyph active reducedMotion={reducedMotion} />
);

const useStyles = makeStyles((theme) => ({
  card: {
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    display: "flex",
    flexDirection: "column",
    minHeight: 459,
    padding: theme.spacing(7, 4),
    textAlign: "center",
    width: "100%",
  },
  iconStage: {
    alignItems: "center",
    display: "flex",
    height: 172,
    justifyContent: "center",
    marginBottom: theme.spacing(4),
    position: "relative",
    width: 172,
  },
  progressRing: {
    borderRadius: "50%",
    height: 150,
    position: "absolute",
    width: 150,
    "&::before": {
      background: theme.palette.grey[200],
      borderRadius: "50%",
      content: '""',
      inset: 5,
      position: "absolute",
    },
    "&::after": {
      background: theme.palette.common.white,
      borderRadius: "50%",
      content: '""',
      inset: 12,
      position: "absolute",
    },
  },
  progressRingAnimated: {
    animation: "$wireDocumentProgress 4s linear infinite",
    background: `conic-gradient(${theme.palette.warning.main} 0 25%, transparent 25% 100%)`,
  },
  progressRingReducedMotion: {
    background: `conic-gradient(${theme.palette.warning.main} 0 75%, transparent 75% 100%)`,
  },
  documentIcon: {
    color: theme.palette.primary.main,
    height: 60,
    position: "relative",
    width: 51,
    zIndex: 1,
  },
  headline: {
    color: theme.palette.primary.main,
    fontSize: 24,
    fontWeight: theme.typography.fontWeightBold,
    letterSpacing: "0.02em",
    lineHeight: "24px",
    marginBottom: theme.spacing(2),
    textTransform: "uppercase",
  },
  subtext: {
    color: theme.palette.primary.main,
    fontSize: 20,
    fontWeight: theme.typography.fontWeightRegular,
    letterSpacing: "0.02em",
    lineHeight: "22px",
    marginBottom: theme.spacing(6),
  },
  steps: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    textAlign: "left",
    width: 286,
  },
  step: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(3),
    padding: theme.spacing(1, 0),
  },
  stepText: {
    color: theme.palette.primary.main,
    fontSize: 16,
    fontWeight: theme.typography.fontWeightRegular,
    letterSpacing: "0.02em",
    lineHeight: "22px",
  },
  "@keyframes wireDocumentProgress": {
    "0%, 24%": {
      background: `conic-gradient(${theme.palette.warning.main} 0 25%, transparent 25% 100%)`,
    },
    "25%, 49%": {
      background: `conic-gradient(${theme.palette.warning.main} 0 50%, transparent 50% 100%)`,
    },
    "50%, 74%": {
      background: `conic-gradient(${theme.palette.warning.main} 0 75%, transparent 75% 100%)`,
    },
    "75%, 100%": {
      background: `conic-gradient(${theme.palette.warning.main} 0 100%, transparent 100% 100%)`,
    },
  },
}));

const BankExtractionProcessingCard = ({ activeStep, reducedMotion }: Props) => {
  const classes = useStyles();

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={bankExtractionCopy.processingHeadline}
      data-testid="bank-extraction-processing-card"
      className={classes.card}
      tabIndex={-1}
    >
      <Box className={classes.iconStage}>
        <Box
          aria-hidden
          className={`${classes.progressRing} ${
            reducedMotion ? classes.progressRingReducedMotion : classes.progressRingAnimated
          }`}
        />
        <DocumentIcon className={classes.documentIcon} aria-hidden />
      </Box>
      <Typography component="h2" className={classes.headline}>
        {bankExtractionCopy.processingHeadline}
      </Typography>
      <Typography className={classes.subtext}>{bankExtractionCopy.processingSubtext}</Typography>
      <Box component="ol" className={classes.steps}>
        {bankExtractionCopy.processingSteps.map((label, index) => {
          const isDone = index < activeStep;
          const isActive = index === activeStep;

          return (
            <Box component="li" key={label} className={classes.step} aria-current={isActive ? "step" : undefined}>
              {isDone ? <CheckGlyph /> : isActive ? <ActiveGlyph reducedMotion={reducedMotion} /> : <PendingGlyph />}
              <Typography className={classes.stepText}>{label}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default memo(BankExtractionProcessingCard);
